import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  ExternalLink,
  Navigation
} from 'lucide-react';

export const ContactSection = () => {
  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'Amtsstraße 2\n48624 Schöppingen',
      link: 'https://www.google.com/maps/search/?api=1&query=Amtsstraße+2+48624+Schöppingen',
      linkText: 'Route planen',
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '02555 3210812\n0176 24076004',
      link: 'tel:025553210812',
      linkText: 'Jetzt anrufen',
    },
    {
      icon: Mail,
      title: 'E-Mail',
      content: 'info@logopaedie-schoeppingen.de',
      link: 'mailto:info@logopaedie-schoeppingen.de',
      linkText: 'E-Mail schreiben',
    },
    {
      icon: Clock,
      title: 'Termine',
      content: 'Nach Vereinbarung\nRufen Sie mich an oder\nschreiben Sie mir eine E-Mail',
      link: null,
      linkText: null,
    },
  ];

  return (
    <section id="kontakt" className="section-padding bg-background relative overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-muted/20 to-muted/40" />

      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            Kontakt
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Anfahrt & Kontakt
          </h2>
          <p className="text-body font-body text-lg leading-relaxed">
            Ich freue mich auf Ihre Kontaktaufnahme. Vereinbaren Sie jetzt einen Termin 
            mit mir.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Contact Cards */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {contactInfo.map((info, index) => (
                <Card 
                  key={info.title}
                  className="bg-card hover:bg-card-elevated border-border hover:border-primary/20 transition-all duration-300"
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-base font-medium text-heading mb-1">
                          {info.title}
                        </h3>
                        <p className="text-body font-body text-sm whitespace-pre-line mb-2">
                          {info.content}
                        </p>
                        {info.link && (
                          <a 
                            href={info.link}
                            className="inline-flex items-center text-primary text-sm font-medium hover:underline"
                            target={info.link.startsWith('http') ? '_blank' : undefined}
                            rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {info.linkText}
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA Card */}
            <Card className="bg-primary/5 border-primary/10">
              <CardContent className="p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-8 h-8 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-medium text-heading mb-2">
                      Termin vereinbaren
                    </h3>
                    <p className="text-body font-body text-sm mb-4">
                      Rufen Sie mich an oder schreiben Sie eine E-Mail – 
                      ich meld mich schnellstmöglich bei Ihnen.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button variant="cta" size="default" asChild>
                        <a href="tel:025553210812">
                          <Phone className="w-4 h-4 mr-2" />
                          Anrufen
                        </a>
                      </Button>
                      <Button variant="outline" size="default" asChild>
                        <a href="mailto:info@logopaedie-schoeppingen.de">
                          <Mail className="w-4 h-4 mr-2" />
                          E-Mail
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map */}
          <div className="relative">
            <Card className="overflow-hidden border-border shadow-elegant h-full">
              <CardContent className="p-0 h-full">
                <div className="relative w-full h-full min-h-[400px] lg:min-h-[500px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2440.5!2d7.2340!3d52.1050!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b83e8d8f5d1a3d%3A0x8a7d1c8b8f5d1a3d!2sAmtsstra%C3%9Fe%202%2C%2048624%20Sch%C3%B6ppingen!5e0!3m2!1sde!2sde!4v1699000000000!5m2!1sde!2sde"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '400px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Standort JaNa Logo Praxis für Logopädie"
                    className="absolute inset-0"
                  />
                  
                  {/* Map Overlay Label */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-border">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                            <Navigation className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <div>
                            <p className="text-heading font-body font-medium text-sm">JaNa Logo</p>
                            <p className="text-caption text-xs">Amtsstraße 2, 48624 Schöppingen</p>
                          </div>
                        </div>
                        <Button variant="soft" size="sm" asChild>
                          <a 
                            href="https://www.google.com/maps/search/?api=1&query=Amtsstraße+2+48624+Schöppingen"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Route
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
